import { useMemo, useCallback } from 'react'
import { Flex, Text, TextField, Select } from '@radix-ui/themes'
import { ClaimFormGeoDataResponse } from '@Generated/back-office-client/src'
import { EDITABLE_CLAIMANT_ROWS, getFieldLabel, InputType } from './claim-form-constants'

type EditableClaimantFieldsProps = {
  data: Record<string, unknown>
  schema: Record<string, unknown>
  geoData: ClaimFormGeoDataResponse
  onChange: (updated: Record<string, unknown>) => void
}

function formatSSN(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 9)
  const match = digits.match(/^(\d{0,3})(\d{0,2})(\d{0,4})$/)
  if (!match) {
    return ''
  }
  const [, area, group, serial] = match
  if (serial) {
    return `${area}-${group}-${serial}`
  }
  if (group) {
    return `${area}-${group}`
  }
  if (area) {
    return area
  }
  return ''
}

export function EditableClaimantFields({
  data,
  schema,
  geoData,
  onChange,
}: EditableClaimantFieldsProps) {
  const updateField = useCallback(
    (field: string, value: unknown) => {
      onChange({ ...data, [field]: value })
    },
    [data, onChange],
  )

  const countryOptions = useMemo(
    () => geoData.countries.map((c) => ({ label: c.name, value: c.name })),
    [geoData],
  )

  const usCitizen = data.usCitizen !== false
  const originalClaimant = data.originalClaimant !== false

  const sectionSchema = (schema as Record<string, unknown>)?.properties as
    | Record<string, unknown>
    | undefined
  const claimantSchema = sectionSchema?.claimant as Record<string, unknown> | undefined
  const claimantProperties = (claimantSchema?.properties ?? {}) as Record<string, unknown>
  const requiredFields = ((claimantSchema?.required ?? []) as string[]) || []

  const hasSchemaField = (field: string): boolean => field in claimantProperties
  const isRequired = (field: string): boolean => requiredFields.includes(field)

  const renderTextField = (field: string, opts?: { type?: InputType; placeholder?: string }) => {
    if (!hasSchemaField(field)) {
      return null
    }
    const label = getFieldLabel(field)
    const required = isRequired(field)

    if (field === 'socialSecurityNumber') {
      return (
        <Flex direction="column" gap="1" className="claim-form-field">
          <Text size="2" color="gray">
            {label} {required && '*'}
          </Text>
          <TextField.Root
            size="2"
            value={String(data[field] ?? '')}
            onChange={(e) => updateField(field, formatSSN(e.target.value))}
            placeholder="123-45-6789"
            maxLength={11}
          />
        </Flex>
      )
    }

    return (
      <Flex direction="column" gap="1" className="claim-form-field">
        <Text size="2" color="gray">
          {label} {required && '*'}
        </Text>
        <TextField.Root
          size="2"
          type={opts?.type ?? 'text'}
          value={String(data[field] ?? '')}
          onChange={(e) => updateField(field, e.target.value)}
          placeholder={opts?.placeholder}
        />
      </Flex>
    )
  }

  const renderBooleanField = (field: string) => {
    if (!hasSchemaField(field)) {
      return null
    }
    const label = getFieldLabel(field)
    const value = data[field] !== false

    return (
      <Flex direction="column" gap="1" className="claim-form-field">
        <Text size="2" color="gray">
          {label}
        </Text>
        <Select.Root
          size="2"
          value={value ? 'yes' : 'no'}
          onValueChange={(v) => updateField(field, v === 'yes')}>
          <Select.Trigger />
          <Select.Content>
            <Select.Item value="yes">Yes</Select.Item>
            <Select.Item value="no">No</Select.Item>
          </Select.Content>
        </Select.Root>
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap="3">
      {EDITABLE_CLAIMANT_ROWS.map(([left, right]) => (
        <div key={left} className="claim-form-two-col">
          {left === 'usCitizen' || left === 'originalClaimant'
            ? renderBooleanField(left)
            : renderTextField(left, left === 'dateOfBirth' ? { type: 'date' } : undefined)}
          {right ? (
            right === 'usCitizen' || right === 'originalClaimant' ? (
              renderBooleanField(right)
            ) : (
              renderTextField(right, right === 'dateOfBirth' ? { type: 'date' } : undefined)
            )
          ) : (
            <div />
          )}
        </div>
      ))}

      {hasSchemaField('countryOfResidence') && !usCitizen && (
        <Flex direction="column" gap="1" className="claim-form-field">
          <Text size="2" color="gray">
            Country of Residence
          </Text>
          <Select.Root
            size="2"
            value={String(data.countryOfResidence ?? '')}
            onValueChange={(v) => updateField('countryOfResidence', v)}>
            <Select.Trigger placeholder="Select Country..." />
            <Select.Content>
              {countryOptions.map((opt) => (
                <Select.Item key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>
      )}

      {!originalClaimant && hasSchemaField('notOriginalPurchaserExplanation') && (
        <Flex direction="column" gap="1" className="claim-form-field">
          <Text size="2" color="gray">
            {getFieldLabel('notOriginalPurchaserExplanation')}{' '}
            {isRequired('notOriginalPurchaserExplanation') && '*'}
          </Text>
          <TextField.Root
            size="2"
            value={String(data.notOriginalPurchaserExplanation ?? '')}
            onChange={(e) => updateField('notOriginalPurchaserExplanation', e.target.value)}
            placeholder="Received as gift"
          />
        </Flex>
      )}
    </Flex>
  )
}
